import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Tooltip,
  styled,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { formattedAmountWithoutAed } from "../../../utils/CommonFunction";
import { statusStyleArray } from "../config";
import { ReactComponent as MwebDhiramIcon } from "../../../assets/svg/MwebDhiramIcon.svg";
import {
  VIEW_MODES,
  getRowCost,
  getRowKey,
  getScopeDisplayName,
  getDetailsRouteId,
  buildProjectDetailsHref,
} from "../utils/projectViewHelpers";

const StyledCard = styled(Card)(() => ({
  margin: "0 !important",
  borderRadius: "0",
  boxShadow: "none",
  borderBottom: "1px solid #D7D8D9",
}));

export const getStatusDisplayText = (text) => {
  if (typeof text !== "string") return text;
  const displayText = text
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return displayText;
};

const formatDateOrDash = (val) => {
  if (val == null || val === "") return "-";
  const m = moment(val);
  if (!m.isValid()) return "-";
  return m.format("MMM DD, YYYY");
};

const ProjectTypePill = ({ managedBy }) => {
  const isContractor = String(managedBy || "").toLowerCase() === "contractor";
  const palette = isContractor
    ? { bg: "#FEF3C7", fg: "#92400E", label: "Contractor" }
    : { bg: "#EDE9FE", fg: "#5B21B6", label: "Reno" };
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        fontSize: 11,
        fontWeight: 600,
        borderRadius: 999,
        backgroundColor: palette.bg,
        color: palette.fg,
        fontFamily: "Inter Tight",
        whiteSpace: "nowrap",
      }}
    >
      {palette.label}
    </Box>
  );
};

export default function MobileProjectCards({
  details,
  loading,
  viewMode = VIEW_MODES.PROJECTS,
  emptyMessage = "No projects available",
}) {
  const navigate = useNavigate();
  const isScopesView = viewMode === VIEW_MODES.SCOPES;

  const handleProjectClick = (item) => {
    const routeId = getDetailsRouteId(item, viewMode);
    if (!routeId) return;

    if (isScopesView) {
      navigate(
        buildProjectDetailsHref(routeId, {
          managedBy: item.parentManagedBy,
          projectId: item.parentProjectId,
          viewMode,
        }),
      );
      return;
    }

    navigate(
      buildProjectDetailsHref(routeId, {
        managedBy: item?.managed_by,
        projectId: item.id,
        viewMode,
      }),
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 200,
        }}
      >
        <Typography variant="body1" sx={{ color: "#6B7280" }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!details || details.length === 0) {
    return (
      <Typography
        variant="body1"
        sx={{ textAlign: "center", marginTop: 4, color: "#6B7280" }}
      >
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <Box sx={{ padding: "0 2vw" }}>
      {details.map((item) => {
        const displayName = isScopesView
          ? getScopeDisplayName(item)
          : item?.name;
        const cost = getRowCost(item);
        const isClickable = Boolean(getDetailsRouteId(item, viewMode));

        return (
          <StyledCard
            key={getRowKey(item, viewMode)}
            onClick={() => isClickable && handleProjectClick(item)}
            sx={{ cursor: isClickable ? "pointer" : "default" }}
          >
            <CardContent sx={{ padding: "16px" }}>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {isScopesView && (
                  <Box>
                    <Typography
                      sx={{ fontSize: "12px", color: "#949DAA", fontWeight: 500 }}
                    >
                      Parent Project
                    </Typography>
                    <Tooltip title={item.parentProjectName || ""} arrow>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "#0D0D0E",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.parentProjectName || "-"}
                      </Typography>
                    </Tooltip>
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 1,
                  }}
                >
                  <Tooltip title={displayName || ""} arrow>
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#0D0D0E",
                        flex: 1,
                        minWidth: 0,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {displayName}
                    </Typography>
                  </Tooltip>
                  <ProjectTypePill managedBy={item?.managed_by} />
                </Box>

                <Box>
                  <Typography
                    sx={{ fontSize: "12px", color: "#949DAA", fontWeight: 500 }}
                  >
                    Location
                  </Typography>
                  <Tooltip title={item?.location || ""} arrow>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 500,
                        color: item?.location ? "#0D0D0E" : "#9CA3AF",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item?.location || "-"}
                    </Typography>
                  </Tooltip>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: "16px",
                    marginBottom: "12px",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar
                      sx={{
                        width: "32px",
                        height: "32px",
                        fontSize: "12px",
                        marginRight: "6px",
                      }}
                      src={item?.user_data?.profile_url}
                    >
                      {item?.user_data?.username?.charAt(0)?.toUpperCase() ||
                        item?.username?.charAt(0)?.toUpperCase() ||
                        "U"}
                    </Avatar>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0px",
                      }}
                    >
                      <Typography sx={{ fontSize: "12px", color: "#949DAA" }}>
                        Customer
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: "500",
                          color: "#0D0D0E",
                        }}
                      >
                        {item?.user_data?.username || item?.username || "-"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "2px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#949DAA",
                      fontWeight: 500,
                    }}
                  >
                    Start/ End Date
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: "500",
                      color: "#0D0D0E",
                    }}
                  >
                    {`${formatDateOrDash(item?.start_date)} - ${formatDateOrDash(item?.end_date)}`}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    margin: ".5vh 0",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{ fontSize: "12px", color: "#949DAA", fontWeight: 500 }}
                    >
                      Cost
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#0D0D0E",
                        marginBottom: "4px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <MwebDhiramIcon />
                      {cost != null
                        ? formattedAmountWithoutAed(cost)
                        : "-"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box
                    sx={{
                      width: "15px",
                      height: "15px",
                      borderRadius: "50%",
                      backgroundColor:
                        statusStyleArray.find(
                          (style) => style.status === item?.status,
                        )?.backgroundColor || "#E5E5E5",
                      marginRight: "6px",
                      border: "3px solid #e5e5e5",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: "500",
                      color: "#0D0D0E",
                    }}
                  >
                    {getStatusDisplayText(item?.status)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        );
      })}
    </Box>
  );
}
